import React, { useState } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import DropDown from '../../components/reusables/DropDown';
import ManualPoolAssignment, {
    isValidPoolAssignment,
    PoolSplit,
} from '../../components/reusables/ManualPoolAssignment';
import { PoolResponse, RuleResponse } from '../../api/generated/models';
import './ManualCatModal.scss';

type AssignmentTab = 'pool' | 'contributions' | 'apportionable' | 'rule';

interface ManuallyCategoriseModalProps {
    visible: boolean;
    selectedRowCount: number;
    onClose: () => void;
    onSavePoolSplits: (
        splits: { poolId: number; splitPercent: number }[]
    ) => void;
    onSaveRule: (ruleId: number) => void;
    pools: PoolResponse[];
    generatedRules: RuleResponse[];
    isLoadingPools: boolean;
    isLoadingRules: boolean;
    isSaving: boolean;
}

const tabs: { label: string; value: AssignmentTab; enabled: boolean }[] = [
    { label: 'Pool Assignment', value: 'pool', enabled: true },
    { label: 'Project Contributions', value: 'contributions', enabled: false },
    { label: 'FA Apportionable', value: 'apportionable', enabled: false },
    { label: 'Generated Rule', value: 'rule', enabled: true },
];

interface PoolAssignmentTabProps {
    pools: PoolResponse[];
    isLoadingPools: boolean;
    poolSplits: PoolSplit[];
    onPoolSplitsChange: (poolSplits: PoolSplit[]) => void;
}

const PoolAssignmentTab: React.FC<PoolAssignmentTabProps> = (props) => (
    <div className="manual-assignment-modal__content">
        <ManualPoolAssignment {...props} />
    </div>
);

interface GeneratedRuleTabProps {
    generatedRules: RuleResponse[];
    isLoadingRules: boolean;
    selectedRule: string;
    onSelectedRuleChange: (ruleId: string) => void;
}

const GeneratedRuleTab: React.FC<GeneratedRuleTabProps> = ({
    generatedRules,
    isLoadingRules,
    selectedRule,
    onSelectedRuleChange,
}) => {
    const ruleOptions = generatedRules
        .filter((rule) => rule.ruleId != null)
        .map((rule) => ({
            label: `${
                rule.projectName ?? `Project ${rule.projectId ?? 0}`
            } (Version ${rule.version ?? 0})`,
            value: String(rule.ruleId),
        }));

    return (
        <div className="manual-assignment-modal__content">
            <label className="manual-assignment-modal__field-label">
                Select generated rule
            </label>
            <DropDown
                data={ruleOptions}
                placeholder={
                    isLoadingRules
                        ? 'Loading generated rules'
                        : 'Choose generated rule'
                }
                value={selectedRule}
                onSelect={(value) => onSelectedRuleChange(value as string)}
            />
        </div>
    );
};

const ManualCatModal: React.FC<ManuallyCategoriseModalProps> = ({
    visible,
    selectedRowCount,
    onClose,
    onSavePoolSplits,
    onSaveRule,
    pools,
    generatedRules,
    isLoadingPools,
    isLoadingRules,
    isSaving,
}) => {
    const [activeTab, setActiveTab] = useState<AssignmentTab>('pool');
    const [poolSplits, setPoolSplits] = useState<PoolSplit[]>([
        { poolId: '', splitPercent: '100' },
    ]);
    const [selectedRule, setSelectedRule] = useState<string>('');

    const isPoolAssignmentValid = isValidPoolAssignment(poolSplits);

    const resetState = () => {
        setActiveTab('pool');
        setPoolSplits([{ poolId: '', splitPercent: '100' }]);
        setSelectedRule('');
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleSave = () => {
        if (activeTab === 'pool' && isPoolAssignmentValid) {
            onSavePoolSplits(
                poolSplits.map((split) => ({
                    poolId: Number(split.poolId),
                    splitPercent: Number(split.splitPercent),
                }))
            );
        }

        if (activeTab === 'rule') {
            const ruleId = Number(selectedRule);
            if (Number.isInteger(ruleId) && ruleId > 0) {
                onSaveRule(ruleId);
            }
        }
    };

    const canSave =
        !isSaving &&
        ((activeTab === 'pool' && isPoolAssignmentValid) ||
            (activeTab === 'rule' && Number(selectedRule) > 0));

    return (
        <Modal
            visible={visible}
            title="Manual Assignment"
            onCancel={handleClose}
            className="manual-assignment-modal overflow-visible"
            modalStyle={{ width: '640px' }}
            footer={
                <div className="manual-assignment-modal__footer">
                    <Button
                        kind="secondary"
                        onClick={handleClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        kind="primary"
                        onClick={handleSave}
                        disabled={!canSave}
                    >
                        Save assignment
                    </Button>
                </div>
            }
        >
            <p className="manual-assignment-modal__selection-summary">
                {selectedRowCount} {selectedRowCount === 1 ? 'asset' : 'assets'}{' '}
                selected
            </p>

            <div className="manual-assignment-modal__tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.value}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.value}
                        aria-disabled={!tab.enabled}
                        className={`manual-assignment-modal__tab${
                            activeTab === tab.value
                                ? ' manual-assignment-modal__tab--active'
                                : ''
                        }`}
                        disabled={!tab.enabled}
                        onClick={() => setActiveTab(tab.value)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'pool' && (
                <PoolAssignmentTab
                    pools={pools}
                    isLoadingPools={isLoadingPools}
                    poolSplits={poolSplits}
                    onPoolSplitsChange={setPoolSplits}
                />
            )}

            {activeTab === 'rule' && (
                <GeneratedRuleTab
                    generatedRules={generatedRules}
                    isLoadingRules={isLoadingRules}
                    selectedRule={selectedRule}
                    onSelectedRuleChange={setSelectedRule}
                />
            )}
        </Modal>
    );
};

export default ManualCatModal;
